import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowLeft,
  Save,
  Shield,
  ChevronRight,
  AlertCircle,
  Building2,
  Link as LinkIcon,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_designation_list,
  saveupdate_designtion,
  saveupdatenew_designtion,
  server_post_data,
  get_department_with_categories,
} from "../ServiceConnection/serviceconnection";
import {
  handleAphabetsChange,
  handleURLChange_Modify,
} from "../CommonJquery/CommonJquery";
import Loader from "../components/layout/Loader";

export default function AddEditRole() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { toast } = useToast();

  const roleId = params.id || location.pathname.split("/")[2];
  const isEditMode = !!roleId;

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [posLandingUrl, setPosLandingUrl] = useState("");
  const [dashboardLandingUrl, setDashboardLandingUrl] = useState("");
  const [mobileAppLandingUrl, setMobileAppLandingUrl] = useState("");
  const [managerAppLandingUrl, setManagerAppLandingUrl] = useState("");
  const [captainAppLandingUrl, setCaptainAppLandingUrl] = useState("");

  // Separate data for POS and Dashboard
  const [posParentData, setPosParentData] = useState([]);
  const [posSubData, setPosSubData] = useState({});
  const [posPermissions, setPosPermissions] = useState({});
  const [dashboardParentData, setDashboardParentData] = useState([]);
  const [dashboardSubData, setDashboardSubData] = useState({});
  const [dashboardPermissions, setDashboardPermissions] = useState({});

  const [mobileAppParentData, setMobileAppParentData] = useState([]);
  const [mobileAppSubData, setMobileAppSubData] = useState({});
  const [mobileAppPermissions, setMobileAppPermissions] = useState({});

  const [managerAppParentData, setManagerAppParentData] = useState([]);
  const [managerAppSubData, setManagerAppSubData] = useState({});
  const [managerAppPermissions, setManagerAppPermissions] = useState({});

  const [captainAppParentData, setCaptainAppParentData] = useState([]);
  const [captainAppSubData, setCaptainAppSubData] = useState({});
  const [captainAppPermissions, setCaptainAppPermissions] = useState({});

  const [editorDataMainID, setEditorDataMainID] = useState("0");
  const [errors, setErrors] = useState({});
  const [openAccordions, setOpenAccordions] = useState([
    "departments",
    "pos",
    "dashboard",
  ]);

  // Department selection - now storing department_id instead of primary_id
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(
        get_department_with_categories,
        fd,
      );

      if (!response.data.error) {
        const deptData = response.data.message.data || [];
        console.log("Fetched departments:", deptData);
        setDepartments(deptData);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, []);

  // Handle select all departments
  const handleSelectAllDepartments = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      setSelectedDepartments((prev) => {
        if (prev.length === departments.length) {
          return [];
        } else {
          return departments.map((d) => d.department_id);
        }
      });
    },
    [departments],
  );

  // Parse inside pages
  const parseInsidePages = useCallback((insidePage) => {
    if (!insidePage || typeof insidePage !== "string") return [];
    return insidePage
      .split("/")
      .filter((page) => page.trim() !== "")
      .map((page) => {
        const [id, label] = page.split("@");
        return { id, label: label || "Unknown" };
      });
  }, []);

  // Handle select all POS permissions
  const handleSelectAllPosPermissions = useCallback(() => {
    const allPermissions = {};

    posParentData.forEach((parent) => {
      allPermissions[parent.page_url_id] = { main: true, actions: {} };

      if (posSubData[parent.id] && posSubData[parent.id].length > 0) {
        posSubData[parent.id].forEach((sub) => {
          allPermissions[sub.page_url_id] = { main: true, actions: {} };

          if (sub.insidepage) {
            const actions = parseInsidePages(sub.insidepage);
            actions.forEach((action) => {
              if (!allPermissions[sub.page_url_id].actions) {
                allPermissions[sub.page_url_id].actions = {};
              }
              allPermissions[sub.page_url_id].actions[action.id] = true;
            });
          }
        });
      }

      if (parent.parent_id === 0 && !parent.dropdown && parent.insidepage) {
        const actions = parseInsidePages(parent.insidepage);
        actions.forEach((action) => {
          if (!allPermissions[parent.page_url_id].actions) {
            allPermissions[parent.page_url_id].actions = {};
          }
          allPermissions[parent.page_url_id].actions[action.id] = true;
        });
      }
    });

    const currentCount = Object.keys(posPermissions).length;
    const totalCount = Object.keys(allPermissions).length;

    if (currentCount === totalCount) {
      setPosPermissions({});
    } else {
      setPosPermissions(allPermissions);
    }
  }, [posParentData, posSubData, posPermissions, parseInsidePages]);

  // Handle select all Dashboard permissions
  const handleSelectAllDashboardPermissions = useCallback(() => {
    const allPermissions = {};

    dashboardParentData.forEach((parent) => {
      allPermissions[parent.page_url_id] = { main: true, actions: {} };

      if (
        dashboardSubData[parent.id] &&
        dashboardSubData[parent.id].length > 0
      ) {
        dashboardSubData[parent.id].forEach((sub) => {
          allPermissions[sub.page_url_id] = { main: true, actions: {} };

          if (sub.insidepage) {
            const actions = parseInsidePages(sub.insidepage);
            actions.forEach((action) => {
              if (!allPermissions[sub.page_url_id].actions) {
                allPermissions[sub.page_url_id].actions = {};
              }
              allPermissions[sub.page_url_id].actions[action.id] = true;
            });
          }
        });
      }

      if (parent.parent_id === 0 && !parent.dropdown && parent.insidepage) {
        const actions = parseInsidePages(parent.insidepage);
        actions.forEach((action) => {
          if (!allPermissions[parent.page_url_id].actions) {
            allPermissions[parent.page_url_id].actions = {};
          }
          allPermissions[parent.page_url_id].actions[action.id] = true;
        });
      }
    });

    const currentCount = Object.keys(dashboardPermissions).length;
    const totalCount = Object.keys(allPermissions).length;

    if (currentCount === totalCount) {
      setDashboardPermissions({});
    } else {
      setDashboardPermissions(allPermissions);
    }
  }, [
    dashboardParentData,
    dashboardSubData,
    dashboardPermissions,
    parseInsidePages,
  ]);
  
  // Handle select all Dashboard permissions
  const handleSelectAllMobileAppPermissions = useCallback(() => {
    const allPermissions = {};

    mobileAppParentData.forEach((parent) => {
      allPermissions[parent.page_url_id] = { main: true, actions: {} };

      if (
        mobileAppSubData[parent.id] &&
        mobileAppSubData[parent.id].length > 0
      ) {
        mobileAppSubData[parent.id].forEach((sub) => {
          allPermissions[sub.page_url_id] = { main: true, actions: {} };

          if (sub.insidepage) {
            const actions = parseInsidePages(sub.insidepage);
            actions.forEach((action) => {
              if (!allPermissions[sub.page_url_id].actions) {
                allPermissions[sub.page_url_id].actions = {};
              }
              allPermissions[sub.page_url_id].actions[action.id] = true;
            });
          }
        });
      }

      if (parent.parent_id === 0 && !parent.dropdown && parent.insidepage) {
        const actions = parseInsidePages(parent.insidepage);
        actions.forEach((action) => {
          if (!allPermissions[parent.page_url_id].actions) {
            allPermissions[parent.page_url_id].actions = {};
          }
          allPermissions[parent.page_url_id].actions[action.id] = true;
        });
      }
    });

    const currentCount = Object.keys(mobileAppPermissions).length;
    const totalCount = Object.keys(allPermissions).length;

    if (currentCount === totalCount) {
      setMobileAppPermissions({});
    } else {
      setMobileAppPermissions(allPermissions);
    }
  }, [
    mobileAppParentData,
    mobileAppSubData,
    mobileAppPermissions,
    parseInsidePages,
  ]);  
  
  
  // Handle select all Dashboard permissions
  const handleSelectAllManagerAppPermissions = useCallback(() => {
    const allPermissions = {};

    managerAppParentData.forEach((parent) => {
      allPermissions[parent.page_url_id] = { main: true, actions: {} };

      if (
        managerAppSubData[parent.id] &&
        managerAppSubData[parent.id].length > 0
      ) {
        managerAppSubData[parent.id].forEach((sub) => {
          allPermissions[sub.page_url_id] = { main: true, actions: {} };

          if (sub.insidepage) {
            const actions = parseInsidePages(sub.insidepage);
            actions.forEach((action) => {
              if (!allPermissions[sub.page_url_id].actions) {
                allPermissions[sub.page_url_id].actions = {};
              }
              allPermissions[sub.page_url_id].actions[action.id] = true;
            });
          }
        });
      }

      if (parent.parent_id === 0 && !parent.dropdown && parent.insidepage) {
        const actions = parseInsidePages(parent.insidepage);
        actions.forEach((action) => {
          if (!allPermissions[parent.page_url_id].actions) {
            allPermissions[parent.page_url_id].actions = {};
          }
          allPermissions[parent.page_url_id].actions[action.id] = true;
        });
      }
    });

    const currentCount = Object.keys(managerAppPermissions).length;
    const totalCount = Object.keys(allPermissions).length;

    if (currentCount === totalCount) {
      setManagerAppPermissions({});
    } else {
      setManagerAppPermissions(allPermissions);
    }
  }, [
    managerAppParentData,
    managerAppSubData,
    managerAppPermissions,
    parseInsidePages,
  ]);
  
  // Handle select all Dashboard permissions
  const handleSelectAllCaptainAppPermissions = useCallback(() => {
    const allPermissions = {};

    captainAppParentData.forEach((parent) => {
      allPermissions[parent.page_url_id] = { main: true, actions: {} };

      if (
        captainAppSubData[parent.id] &&
        captainAppSubData[parent.id].length > 0
      ) {
        captainAppSubData[parent.id].forEach((sub) => {
          allPermissions[sub.page_url_id] = { main: true, actions: {} };

          if (sub.insidepage) {
            const actions = parseInsidePages(sub.insidepage);
            actions.forEach((action) => {
              if (!allPermissions[sub.page_url_id].actions) {
                allPermissions[sub.page_url_id].actions = {};
              }
              allPermissions[sub.page_url_id].actions[action.id] = true;
            });
          }
        });
      }

      if (parent.parent_id === 0 && !parent.dropdown && parent.insidepage) {
        const actions = parseInsidePages(parent.insidepage);
        actions.forEach((action) => {
          if (!allPermissions[parent.page_url_id].actions) {
            allPermissions[parent.page_url_id].actions = {};
          }
          allPermissions[parent.page_url_id].actions[action.id] = true;
        });
      }
    });

    const currentCount = Object.keys(captainAppPermissions).length;
    const totalCount = Object.keys(allPermissions).length;

    if (currentCount === totalCount) {
      setCaptainAppPermissions({});
    } else {
      setCaptainAppPermissions(allPermissions);
    }
  }, [
    captainAppParentData,
    captainAppSubData,
    captainAppPermissions,
    parseInsidePages,
  ]);

  // Fetch role data
  const fetchRoleData = useCallback(
    async (callId = 0, flag = 1) => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("call_id", callId);
      fd.append("flag", flag);

      try {
        const response = await server_post_data(get_designation_list, fd);

        if (!response.data.error) {
          const data = response.data.message;
          console.log("response.data.message", response.data.message);
          // Process parent and sub data - FILTER BY PANEL
          const posParentArray = [];
          const dashboardParentArray = [];
          const mobileAppParentArray = [];
          const managerAppParentArray = [];
          const captainAppParentArray = [];
          const posSubObject = {};
          const dashboardSubObject = {};
          const mobileAppSubObject = {};
          const managerAppSubObject = {};
          const captainAppSubObject = {};


          data.data.forEach((item) => {
            const isPosPanel = item.panel_name === "POS" || item.panel_name === null;
            const isDashboardPanel = item.panel_name === "Dashboard";
            const isMobileAppPanel = item.panel_name === "Mobile Pos";
            const isManagerAppPanel = item.panel_name === "Manager App";
            const isCaptainAppPanel = item.panel_name === "Captain App";

            if (item.parent_id === 0) {
              const parentItem = {
                id: item.id,
                page_name: item.page_name,
                page_url_id: item.page_url_id,
                parent_id: item.parent_id,
                insidepage: item.insidepage,
                dropdown: item.dropdown,
                flag: item.flag,
                panel_name: item.panel_name,
              };

              if (isPosPanel) {
                posParentArray.push(parentItem);
              }
              if (isDashboardPanel) {
                dashboardParentArray.push(parentItem);
              }
              if (isMobileAppPanel) {
                mobileAppParentArray.push(parentItem);
              }
              if (isManagerAppPanel) {
                managerAppParentArray.push(parentItem);
              }
              if (isCaptainAppPanel) {
                captainAppParentArray.push(parentItem);
              }
            } else {
              const subItem = {
                id: item.id,
                page_name: item.page_name,
                page_url_id: item.page_url_id,
                parent_id: item.parent_id,
                insidepage: item.insidepage,
                dropdown: item.dropdown,
                flag: item.flag,
                panel_name: item.panel_name,
              };

              if (isPosPanel) {
                if (!posSubObject[item.parent_id]) {
                  posSubObject[item.parent_id] = [];
                }
                posSubObject[item.parent_id].push(subItem);
              }
              if (isDashboardPanel) {
                if (!dashboardSubObject[item.parent_id]) {
                  dashboardSubObject[item.parent_id] = [];
                }
                dashboardSubObject[item.parent_id].push(subItem);
              }
              if (isMobileAppPanel) {
                if (!mobileAppSubObject[item.parent_id]) {
                  mobileAppSubObject[item.parent_id] = [];
                }
                mobileAppSubObject[item.parent_id].push(subItem);
              }
              if (isManagerAppPanel) {
                if (!managerAppSubObject[item.parent_id]) {
                  managerAppSubObject[item.parent_id] = [];
                }
                managerAppSubObject[item.parent_id].push(subItem);
              }
              if (isCaptainAppPanel) {
                if (!captainAppSubObject[item.parent_id]) {
                  captainAppSubObject[item.parent_id] = [];
                }
                captainAppSubObject[item.parent_id].push(subItem);
              }
            }
          });

          setPosParentData(posParentArray);
          setPosSubData(posSubObject);
          setDashboardParentData(dashboardParentArray);
          setDashboardSubData(dashboardSubObject);
          setMobileAppParentData(mobileAppParentArray);
          setMobileAppSubData(mobileAppSubObject);
          setManagerAppParentData(managerAppParentArray);
          setManagerAppSubData(managerAppSubObject);
          setCaptainAppParentData(captainAppParentArray);
          setCaptainAppSubData(captainAppSubObject);

          // Process existing data for edit mode
          if (data.designation && data.designation.length > 0) {
            const role = data.designation[0];
            setRoleName(role.designation_name);
            setPosLandingUrl(role.pos_landing_url || "");
            setDashboardLandingUrl(role.dashboard_landing_url || "");
            setEditorDataMainID(role.primary_id);

            // Parse departments
            if (role.department_ids) {
              const deptIds = role.department_ids
                .split(",")
                .map((id) => parseInt(id.trim()));
              setSelectedDepartments(deptIds);
            }

            // Parse POS permissions
            if (role.pos_access_list) {
              const posAccessList = role.pos_access_list.split("/");
              const posPermissionsMap = {};

              posAccessList.forEach((item) => {
                if (item.includes("@")) {
                  const [page, ...extras] = item.split("@");
                  if (!posPermissionsMap[page]) {
                    posPermissionsMap[page] = { main: true, actions: {} };
                  }
                  extras.forEach((extra) => {
                    posPermissionsMap[page].actions[extra] = true;
                  });
                } else if (item) {
                  posPermissionsMap[item] = { main: true, actions: {} };
                }
              });

              setPosPermissions(posPermissionsMap);
              console.log("posPermissionsMap", posPermissionsMap);
            }

            // Parse Dashboard permissions
            if (role.dashboard_access_list) {
              const dashAccessList = role.dashboard_access_list.split("/");
              const dashPermissionsMap = {};

              dashAccessList.forEach((item) => {
                if (item.includes("@")) {
                  const [page, ...extras] = item.split("@");
                  if (!dashPermissionsMap[page]) {
                    dashPermissionsMap[page] = { main: true, actions: {} };
                  }
                  extras.forEach((extra) => {
                    dashPermissionsMap[page].actions[extra] = true;
                  });
                } else if (item) {
                  dashPermissionsMap[item] = { main: true, actions: {} };
                }
              });

              setDashboardPermissions(dashPermissionsMap);
              console.log("dashPermissionsMap", dashPermissionsMap);
            }
          }
        } else {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching role:", error);
        toast({
          title: "Error",
          description: "Failed to load role data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast],
  );

  // Handle POS permission toggle
  const handlePosPermissionToggle = useCallback((pageUrlId, action = null) => {
    setPosPermissions((prev) => {
      const updated = { ...prev };

      if (action) {
        if (!updated[pageUrlId]) {
          updated[pageUrlId] = { main: true, actions: {} };
        }
        if (!updated[pageUrlId].actions) {
          updated[pageUrlId].actions = {};
        }
        updated[pageUrlId].actions[action] =
          !updated[pageUrlId].actions[action];
      } else {
        if (updated[pageUrlId]) {
          delete updated[pageUrlId];
        } else {
          updated[pageUrlId] = { main: true, actions: {} };
        }
      }

      return updated;
    });
  }, []);

  // Handle Dashboard permission toggle
  const handleDashboardPermissionToggle = useCallback(
    (pageUrlId, action = null) => {
      setDashboardPermissions((prev) => {
        const updated = { ...prev };

        if (action) {
          if (!updated[pageUrlId]) {
            updated[pageUrlId] = { main: true, actions: {} };
          }
          if (!updated[pageUrlId].actions) {
            updated[pageUrlId].actions = {};
          }
          updated[pageUrlId].actions[action] =
            !updated[pageUrlId].actions[action];
        } else {
          if (updated[pageUrlId]) {
            delete updated[pageUrlId];
          } else {
            updated[pageUrlId] = { main: true, actions: {} };
          }
        }

        return updated;
      });
    },
    [],
  );

  // Handle Mobile App permission toggle
  const handleMobileAppPermissionToggle = useCallback(
    (pageUrlId, action = null) => {
      setMobileAppPermissions((prev) => {
        const updated = { ...prev };

        if (action) {
          if (!updated[pageUrlId]) {
            updated[pageUrlId] = { main: true, actions: {} };
          }
          if (!updated[pageUrlId].actions) {
            updated[pageUrlId].actions = {};
          }
          updated[pageUrlId].actions[action] =
            !updated[pageUrlId].actions[action];
        } else {
          if (updated[pageUrlId]) {
            delete updated[pageUrlId];
          } else {
            updated[pageUrlId] = { main: true, actions: {} };
          }
        }

        return updated;
      });
    },
    [],
  );


    // Handle Manager App permission toggle
  const handleManagerAppPermissionToggle = useCallback(
    (pageUrlId, action = null) => {
      setManagerAppPermissions((prev) => {
        const updated = { ...prev };

        if (action) {
          if (!updated[pageUrlId]) {
            updated[pageUrlId] = { main: true, actions: {} };
          }
          if (!updated[pageUrlId].actions) {
            updated[pageUrlId].actions = {};
          }
          updated[pageUrlId].actions[action] =
            !updated[pageUrlId].actions[action];
        } else {
          if (updated[pageUrlId]) {
            delete updated[pageUrlId];
          } else {
            updated[pageUrlId] = { main: true, actions: {} };
          }
        }

        return updated;
      });
    },
    [],
  );

    // Handle Captain App permission toggle
  const handleCaptainAppPermissionToggle = useCallback(
    (pageUrlId, action = null) => {
      setCaptainAppPermissions((prev) => {
        const updated = { ...prev };

        if (action) {
          if (!updated[pageUrlId]) {
            updated[pageUrlId] = { main: true, actions: {} };
          }
          if (!updated[pageUrlId].actions) {
            updated[pageUrlId].actions = {};
          }
          updated[pageUrlId].actions[action] =
            !updated[pageUrlId].actions[action];
        } else {
          if (updated[pageUrlId]) {
            delete updated[pageUrlId];
          } else {
            updated[pageUrlId] = { main: true, actions: {} };
          }
        }

        return updated;
      });
    },
    [],
  );

  // Check if POS permission is enabled
  const isPosPermissionEnabled = useCallback(
    (pageUrlId, action = null) => {
      if (!posPermissions[pageUrlId]) return false;
      if (!action) return posPermissions[pageUrlId].main;
      return posPermissions[pageUrlId].actions?.[action] || false;
    },
    [posPermissions],
  );

  // Check if Dashboard permission is enabled
  const isDashboardPermissionEnabled = useCallback(
    (pageUrlId, action = null) => {
      if (!dashboardPermissions[pageUrlId]) return false;
      if (!action) return dashboardPermissions[pageUrlId].main;
      return dashboardPermissions[pageUrlId].actions?.[action] || false;
    },
    [dashboardPermissions],
  );

   // Check if Mobile App permission is enabled
  const isMobileAppPermissionEnabled = useCallback(
    (pageUrlId, action = null) => {
      if (!mobileAppPermissions[pageUrlId]) return false;
      if (!action) return mobileAppPermissions[pageUrlId].main;
      return mobileAppPermissions[pageUrlId].actions?.[action] || false;
    },
    [mobileAppPermissions],
  );

   // Check if Manager App permission is enabled
  const isManagerAppPermissionEnabled = useCallback(
    (pageUrlId, action = null) => {
      if (!managerAppPermissions[pageUrlId]) return false;
      if (!action) return managerAppPermissions[pageUrlId].main;
      return managerAppPermissions[pageUrlId].actions?.[action] || false;
    },
    [managerAppPermissions],
  );

   // Check if Captain App permission is enabled
  const isCaptainAppPermissionEnabled = useCallback(
    (pageUrlId, action = null) => {
      if (!captainAppPermissions[pageUrlId]) return false;
      if (!action) return captainAppPermissions[pageUrlId].main;
      return captainAppPermissions[pageUrlId].actions?.[action] || false;
    },
    [captainAppPermissions],
  );

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!roleName.trim()) {
      newErrors.roleName = "Role name is required";
    } else if (roleName.trim().length < 3) {
      newErrors.roleName = "Role name must be at least 3 characters";
    }

    if (selectedDepartments.length === 0) {
      newErrors.departments = "Please select at least one department";
    }

    const posPermissionCount = Object.keys(posPermissions).length;
    const dashboardPermissionCount = Object.keys(dashboardPermissions).length;
    const mobileAppPermissionCount = Object.keys(mobileAppPermissions).length;
    const managerAppPermissionCount = Object.keys(managerAppPermissions).length;
    const captainAppPermissionCount = Object.keys(captainAppPermissions).length;

    if (posPermissionCount === 0 && dashboardPermissionCount === 0) {
      newErrors.permissions =
        "Please select at least one permission for POS or Dashboard";
    }

    if (posPermissionCount > 0 && !posLandingUrl.trim()) {
      newErrors.posLandingUrl =
        "POS landing URL is required when POS permissions are selected";
    }

    if (dashboardPermissionCount > 0 && !dashboardLandingUrl.trim()) {
      newErrors.dashboardLandingUrl =
        "Dashboard landing URL is required when Dashboard permissions are selected";
    }

    if (mobileAppPermissionCount > 0 && !mobileAppLandingUrl.trim()) {
      newErrors.mobileAppLandingUrl =
        "Mobile App landing URL is required when Mobile App permissions are selected";
    }

    if (managerAppPermissionCount > 0 && !managerAppLandingUrl.trim()) {
      newErrors.managerAppLandingUrl =
        "Manager App landing URL is required when Manager App permissions are selected";
    }

    if (captainAppPermissionCount > 0 && !captainAppLandingUrl.trim()) {
      newErrors.captainAppLandingUrl =
        "Captain App landing URL is required when Captain App permissions are selected";
    }

    console.log(captainAppLandingUrl);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    roleName,
    selectedDepartments,
    posPermissions,
    dashboardPermissions,
    posLandingUrl,
    dashboardLandingUrl,
  ]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields and select permissions",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    // Build POS access list
    let posAccessList = "";
    Object.keys(posPermissions).forEach((pageUrlId) => {
      const permission = posPermissions[pageUrlId];
      posAccessList += "/" + pageUrlId;

      if (permission.actions && Object.keys(permission.actions).length > 0) {
        Object.keys(permission.actions).forEach((action) => {
          if (permission.actions[action]) {
            posAccessList += "@" + action;
          }
        });
      }
    });

    if (posAccessList.startsWith("/")) {
      posAccessList = posAccessList.substring(1);
    }

    // Build Dashboard access list
    let dashboardAccessList = "";
    Object.keys(dashboardPermissions).forEach((pageUrlId) => {
      const permission = dashboardPermissions[pageUrlId];
      dashboardAccessList += "/" + pageUrlId;

      if (permission.actions && Object.keys(permission.actions).length > 0) {
        Object.keys(permission.actions).forEach((action) => {
          if (permission.actions[action]) {
            dashboardAccessList += "@" + action;
          }
        });
      }
    });

    if (dashboardAccessList.startsWith("/")) {
      dashboardAccessList = dashboardAccessList.substring(1);
    }

    // Build Dashboard access list
    let MobileAppAccessList = "";
    Object.keys(mobileAppPermissions).forEach((pageUrlId) => {
      const permission = mobileAppPermissions[pageUrlId];
      MobileAppAccessList += "/" + pageUrlId;

      if (permission.actions && Object.keys(permission.actions).length > 0) {
        Object.keys(permission.actions).forEach((action) => {
          if (permission.actions[action]) {
            MobileAppAccessList += "@" + action;
          }
        });
      }
    });

    if (MobileAppAccessList.startsWith("/")) {
      MobileAppAccessList = MobileAppAccessList.substring(1);
    }


    // Build Dashboard access list
    let ManagerAppAccessList = "";
    Object.keys(managerAppPermissions).forEach((pageUrlId) => {
      const permission = managerAppPermissions[pageUrlId];
      ManagerAppAccessList += "/" + pageUrlId;

      if (permission.actions && Object.keys(permission.actions).length > 0) {
        Object.keys(permission.actions).forEach((action) => {
          if (permission.actions[action]) {
            ManagerAppAccessList += "@" + action;
          }
        });
      }
    });

    if (ManagerAppAccessList.startsWith("/")) {
      ManagerAppAccessList = ManagerAppAccessList.substring(1);
    }


    // Build Dashboard access list
    let CaptainAppAccessList = "";
    Object.keys(captainAppPermissions).forEach((pageUrlId) => {
      const permission = captainAppPermissions[pageUrlId];
      CaptainAppAccessList += "/" + pageUrlId;

      if (permission.actions && Object.keys(permission.actions).length > 0) {
        Object.keys(permission.actions).forEach((action) => {
          if (permission.actions[action]) {
            CaptainAppAccessList += "@" + action;
          }
        });
      }
    });

    if (CaptainAppAccessList.startsWith("/")) {
      CaptainAppAccessList = CaptainAppAccessList.substring(1);
    }

    const fd = new FormData();
    fd.append("main_id", editorDataMainID);
    fd.append("right_name", roleName.trim());
    fd.append("pos_landing_url", posLandingUrl.trim());
    fd.append("dashboard_landing_url", dashboardLandingUrl.trim());
    fd.append("mobile_app_landing_url", mobileAppLandingUrl.trim());
    fd.append("manager_app_landing_url", managerAppLandingUrl.trim());
    fd.append("captain_app_landing_url", captainAppLandingUrl.trim());
    fd.append("department_ids", selectedDepartments.join(","));
    fd.append("pos_access_list", posAccessList);
    fd.append("dashboard_access_list", dashboardAccessList);
    fd.append("mobile_app_access_list", MobileAppAccessList);
    fd.append("manager_app_access_list", ManagerAppAccessList);
    fd.append("captain_app_access_list", CaptainAppAccessList);

    try {
      const response = await server_post_data(saveupdatenew_designtion, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save role",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: isEditMode
            ? "Role updated successfully"
            : "Role created successfully",
        });
        //navigate("/roles");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    validateForm,
    roleName,
    posLandingUrl,
    dashboardLandingUrl,
    mobileAppLandingUrl,
    managerAppLandingUrl,
    captainAppLandingUrl,
    selectedDepartments,
    posPermissions,
    dashboardPermissions,
    mobileAppPermissions,
    managerAppPermissions,
    captainAppPermissions,
    editorDataMainID,
    isEditMode,
    toast,
    navigate,
  ]);

  // Initial load
  useEffect(() => {
    fetchDepartments();
    if (roleId) {
      fetchRoleData(roleId, 2);
    } else {
      fetchRoleData(0, 1);
    }
  }, [roleId, fetchRoleData, fetchDepartments]);

  console.log(posPermissions, dashboardPermissions);

  return (
    <MainLayout>
      <Header
        title={isEditMode ? "Edit Role" : "Create New Role"}
        subtitle={
          isEditMode
            ? "Update role permissions and access"
            : "Define a new role with specific permissions"
        }
      />

      <div className="p-6 space-y-6">
        {/* Back Button */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate("/roles")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Roles
          </Button>
        </div>

        {/* Role Information */}
        <Card>
          <CardContent className="pt-6">
            <div className="max-w-md space-y-2">
              <Label>
                Role Name <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="e.g., Manager, Chef, Waiter"
                value={roleName}
                onChange={(e) => {
                  handleAphabetsChange(e);
                  setRoleName(e.target.value);
                  setErrors((prev) => {
                    const { roleName, ...rest } = prev;
                    return rest;
                  });
                }}
                className={errors.roleName ? "border-destructive" : ""}
                maxLength={75}
                minLength={3}
              />
              {errors.roleName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.roleName}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Accordion for all sections */}
        <Accordion
          type="multiple"
          value={openAccordions}
          onValueChange={setOpenAccordions}
          className="space-y-4"
        >
          {/* Department Selection Accordion */}
          <AccordionItem value="departments" className="border rounded-lg">
            <Card className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    <div className="text-left">
                      <p className="font-semibold">Assign Departments</p>
                      <p className="text-sm text-muted-foreground font-normal">
                        Select which departments this role can access
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {selectedDepartments.length} of {departments.length}{" "}
                      selected
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent className="pt-0">
                  <div className="flex justify-end mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllDepartments}
                      className="gap-2"
                    >
                      {selectedDepartments.length === departments.length
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  {errors.departments && (
                    <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {errors.departments}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {departments.map((dept) => {
                      const isSelected = selectedDepartments.includes(
                        dept.department_id,
                      );

                      return (
                        <label
                          key={dept.department_id}
                          htmlFor={`dept-${dept.department_id}`}
                          className={cn(
                            "flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors",
                            isSelected
                              ? "border-primary bg-primary/10"
                              : "border-border hover:border-primary/50 hover:bg-muted",
                          )}
                        >
                          <input
                            type="checkbox"
                            id={`dept-${dept.department_id}`}
                            checked={isSelected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedDepartments((prev) => {
                                if (checked) {
                                  return [...prev, dept.department_id];
                                } else {
                                  return prev.filter(
                                    (id) => id !== dept.department_id,
                                  );
                                }
                              });
                            }}
                            className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary focus:ring-2"
                          />
                          <span className="text-sm font-medium flex-1">
                            {dept.department_name}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {departments.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No departments available. Please create departments first.
                    </p>
                  )}

                  {errors.permissions && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {errors.permissions}
                      </p>
                    </div>
                  )}
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>

          {/* POS Permissions Accordion */}
          <AccordionItem value="pos" className="border rounded-lg">
            <Card className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <div className="text-left">
                      <p className="font-semibold">POS Permissions</p>
                      <p className="text-sm text-muted-foreground font-normal">
                        Choose which sections and actions this role can access
                        in POS
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {Object.keys(posPermissions).length} permission(s)
                      selected
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent className="pt-0 space-y-6">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllPosPermissions}
                      className="gap-2"
                    >
                      {Object.keys(posPermissions).length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  

                  <div className="space-y-4">
                    {posParentData.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No POS permissions available
                      </p>
                    ) : (
                      posParentData.map((parent) => (
                        <div
                          key={parent.id}
                          className="border rounded-lg overflow-hidden"
                        >
                          {/* Parent Permission */}
                          <div
                            className={cn(
                              "p-4 bg-muted/30 border-b transition-colors",
                              isPosPermissionEnabled(parent.page_url_id) &&
                                "bg-primary/5",
                            )}
                          >
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`pos-parent-${parent.id}`}
                                checked={isPosPermissionEnabled(
                                  parent.page_url_id,
                                )}
                                onCheckedChange={() =>
                                  handlePosPermissionToggle(parent.page_url_id)
                                }
                              />
                              <Label
                                htmlFor={`pos-parent-${parent.id}`}
                                className="text-base font-semibold cursor-pointer flex-1"
                              >
                                {parent.page_name}
                              </Label>
                              {isPosPermissionEnabled(parent.page_url_id) && (
                                <Shield className="w-4 h-4 text-primary" />
                              )}
                            </div>
                          </div>

                          {/* Sub Permissions */}
                          {posSubData[parent.id] &&
                            posSubData[parent.id].length > 0 && (
                              <div className="p-4 space-y-4">
                                {posSubData[parent.id].map((sub) => (
                                  <div key={sub.id} className="space-y-3">
                                    <div className="flex items-center space-x-2 pl-4">
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                      <Checkbox
                                        id={`pos-sub-${parent.id}-${sub.id}`}
                                        checked={isPosPermissionEnabled(
                                          sub.page_url_id,
                                        )}
                                        onCheckedChange={() =>
                                          handlePosPermissionToggle(
                                            sub.page_url_id,
                                          )
                                        }
                                      />
                                      <Label
                                        htmlFor={`pos-sub-${parent.id}-${sub.id}`}
                                        className="font-medium cursor-pointer flex-1"
                                      >
                                        {sub.page_name}
                                      </Label>
                                    </div>

                                    {sub.insidepage && (
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-12">
                                        {parseInsidePages(sub.insidepage).map(
                                          (action, actionIndex) => (
                                            <div
                                              key={actionIndex}
                                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                            >
                                              <Checkbox
                                                id={`pos-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                checked={isPosPermissionEnabled(
                                                  sub.page_url_id,
                                                  action.id,
                                                )}
                                                onCheckedChange={() =>
                                                  handlePosPermissionToggle(
                                                    sub.page_url_id,
                                                    action.id,
                                                  )
                                                }
                                              />
                                              <Label
                                                htmlFor={`pos-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                className="text-sm cursor-pointer"
                                              >
                                                {action.label}
                                              </Label>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                          {parent.parent_id === 0 &&
                            !parent.dropdown &&
                            parent.insidepage && (
                              <div className="p-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-4">
                                  {parseInsidePages(parent.insidepage).map(
                                    (action, actionIndex) => (
                                      <div
                                        key={actionIndex}
                                        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                      >
                                        <Checkbox
                                          id={`pos-parent-action-${parent.id}-${actionIndex}`}
                                          checked={isPosPermissionEnabled(
                                            parent.page_url_id,
                                            action.id,
                                          )}
                                          onCheckedChange={() =>
                                            handlePosPermissionToggle(
                                              parent.page_url_id,
                                              action.id,
                                            )
                                          }
                                        />
                                        <Label
                                          htmlFor={`pos-parent-action-${parent.id}-${actionIndex}`}
                                          className="text-sm cursor-pointer"
                                        >
                                          {action.label}
                                        </Label>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* POS Landing URL */}
                  {Object.keys(posPermissions).length > 0 && (
                    <div className="pt-4 border-t space-y-2">
                      <Label>
                        POS Landing URL{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="pos-dashboard or orders"
                          value={posLandingUrl}
                          onChange={(e) => {
                            handleURLChange_Modify(e);
                            setPosLandingUrl(e.target.value);
                            setErrors((prev) => {
                              const { posLandingUrl, ...rest } = prev;
                              return rest;
                            });
                          }}
                          className={cn(
                            "pl-9",
                            errors.posLandingUrl && "border-destructive",
                          )}
                        />
                      </div>
                      {errors.posLandingUrl && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.posLandingUrl}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        The page where users will land when accessing POS
                      </p>
                    </div>
                  )}
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>

          {/* Dashboard Permissions Accordion */}
          <AccordionItem value="dashboard" className="border rounded-lg">
            <Card className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-success" />
                    <div className="text-left">
                      <p className="font-semibold">Dashboard Permissions</p>
                      <p className="text-sm text-muted-foreground font-normal">
                        Choose which sections and actions this role can access
                        in Dashboard
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {Object.keys(dashboardPermissions).length} permission(s)
                      selected
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent className="pt-0 space-y-6">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllDashboardPermissions}
                      className="gap-2"
                    >
                      {Object.keys(dashboardPermissions).length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {dashboardParentData.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No Dashboard permissions available
                      </p>
                    ) : (
                      dashboardParentData.map((parent) => (
                        <div
                          key={parent.id}
                          className="border rounded-lg overflow-hidden"
                        >
                          <div
                            className={cn(
                              "p-4 bg-muted/30 border-b transition-colors",
                              isDashboardPermissionEnabled(
                                parent.page_url_id,
                              ) && "bg-success/5",
                            )}
                          >
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`dash-parent-${parent.id}`}
                                checked={isDashboardPermissionEnabled(
                                  parent.page_url_id,
                                )}
                                onCheckedChange={() =>
                                  handleDashboardPermissionToggle(
                                    parent.page_url_id,
                                  )
                                }
                              />
                              <Label
                                htmlFor={`dash-parent-${parent.id}`}
                                className="text-base font-semibold cursor-pointer flex-1"
                              >
                                {parent.page_name}
                              </Label>
                              {isDashboardPermissionEnabled(
                                parent.page_url_id,
                              ) && <Shield className="w-4 h-4 text-success" />}
                            </div>
                          </div>

                          {dashboardSubData[parent.id] &&
                            dashboardSubData[parent.id].length > 0 && (
                              <div className="p-4 space-y-4">
                                {dashboardSubData[parent.id].map((sub) => (
                                  <div key={sub.id} className="space-y-3">
                                    <div className="flex items-center space-x-2 pl-4">
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                      <Checkbox
                                        id={`dash-sub-${parent.id}-${sub.id}`}
                                        checked={isDashboardPermissionEnabled(
                                          sub.page_url_id,
                                        )}
                                        onCheckedChange={() =>
                                          handleDashboardPermissionToggle(
                                            sub.page_url_id,
                                          )
                                        }
                                      />
                                      <Label
                                        htmlFor={`dash-sub-${parent.id}-${sub.id}`}
                                        className="font-medium cursor-pointer flex-1"
                                      >
                                        {sub.page_name}
                                      </Label>
                                    </div>

                                    {sub.insidepage && (
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-12">
                                        {parseInsidePages(sub.insidepage).map(
                                          (action, actionIndex) => (
                                            <div
                                              key={actionIndex}
                                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                            >
                                              <Checkbox
                                                id={`dash-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                checked={isDashboardPermissionEnabled(
                                                  sub.page_url_id,
                                                  action.id,
                                                )}
                                                onCheckedChange={() =>
                                                  handleDashboardPermissionToggle(
                                                    sub.page_url_id,
                                                    action.id,
                                                  )
                                                }
                                              />
                                              <Label
                                                htmlFor={`dash-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                className="text-sm cursor-pointer"
                                              >
                                                {action.label}
                                              </Label>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                          {parent.parent_id === 0 &&
                            !parent.dropdown &&
                            parent.insidepage && (
                              <div className="p-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-4">
                                  {parseInsidePages(parent.insidepage).map(
                                    (action, actionIndex) => (
                                      <div
                                        key={actionIndex}
                                        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                      >
                                        <Checkbox
                                          id={`dash-parent-action-${parent.id}-${actionIndex}`}
                                          checked={isDashboardPermissionEnabled(
                                            parent.page_url_id,
                                            action.id,
                                          )}
                                          onCheckedChange={() =>
                                            handleDashboardPermissionToggle(
                                              parent.page_url_id,
                                              action.id,
                                            )
                                          }
                                        />
                                        <Label
                                          htmlFor={`dash-parent-action-${parent.id}-${actionIndex}`}
                                          className="text-sm cursor-pointer"
                                        >
                                          {action.label}
                                        </Label>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Dashboard Landing URL */}
                  {Object.keys(dashboardPermissions).length > 0 && (
                    <div className="pt-4 border-t space-y-2">
                      <Label>
                        Dashboard Landing URL{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="/dashboard or /analytics"
                          value={dashboardLandingUrl}
                          onChange={(e) => {
                            handleURLChange_Modify(e);
                            setDashboardLandingUrl(e.target.value);
                            setErrors((prev) => {
                              const { dashboardLandingUrl, ...rest } = prev;
                              return rest;
                            });
                          }}
                          className={cn(
                            "pl-9",
                            errors.dashboardLandingUrl && "border-destructive",
                          )}
                        />
                      </div>
                      {errors.dashboardLandingUrl && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.dashboardLandingUrl}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        The page where users will land when accessing Dashboard
                      </p>
                    </div>
                  )}
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>

          {/* Mobile Pos Permissions Accordion - Start */}
          <AccordionItem value="mobile_app" className="border rounded-lg">
            <Card className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-success" />
                    <div className="text-left">
                      <p className="font-semibold">Mobile Pos Permissions</p>
                      <p className="text-sm text-muted-foreground font-normal">
                        Choose which sections and actions this role can access
                        in Mobile Pos
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {Object.keys(mobileAppPermissions).length} permission(s)
                      selected
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent className="pt-0 space-y-6">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllMobileAppPermissions}
                      className="gap-2"
                    >
                      {Object.keys(mobileAppPermissions).length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {mobileAppParentData.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No Mobile App permissions available
                      </p>
                    ) : (
                      mobileAppParentData.map((parent) => (
                        <div
                          key={parent.id}
                          className="border rounded-lg overflow-hidden"
                        >
                          <div
                            className={cn(
                              "p-4 bg-muted/30 border-b transition-colors",
                              isMobileAppPermissionEnabled(
                                parent.page_url_id,
                              ) && "bg-success/5",
                            )}
                          >
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`mobile-app-parent-${parent.id}`}
                                checked={isMobileAppPermissionEnabled(
                                  parent.page_url_id,
                                )}
                                onCheckedChange={() =>
                                  handleMobileAppPermissionToggle(
                                    parent.page_url_id,
                                  )
                                }
                              />
                              <Label
                                htmlFor={`mobile-app-parent-${parent.id}`}
                                className="text-base font-semibold cursor-pointer flex-1"
                              >
                                {parent.page_name}
                              </Label>
                              {isMobileAppPermissionEnabled(
                                parent.page_url_id,
                              ) && <Shield className="w-4 h-4 text-success" />}
                            </div>
                          </div>

                          {mobileAppSubData[parent.id] &&
                            mobileAppSubData[parent.id].length > 0 && (
                              <div className="p-4 space-y-4">
                                {mobileAppSubData[parent.id].map((sub) => (
                                  <div key={sub.id} className="space-y-3">
                                    <div className="flex items-center space-x-2 pl-4">
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                      <Checkbox
                                        id={`mobile-app-sub-${parent.id}-${sub.id}`}
                                        checked={isMobileAppPermissionEnabled(
                                          sub.page_url_id,
                                        )}
                                        onCheckedChange={() =>
                                          handleMobileAppPermissionToggle(
                                            sub.page_url_id,
                                          )
                                        }
                                      />
                                      <Label
                                        htmlFor={`mobile-app-sub-${parent.id}-${sub.id}`}
                                        className="font-medium cursor-pointer flex-1"
                                      >
                                        {sub.page_name}
                                      </Label>
                                    </div>

                                    {sub.insidepage && (
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-12">
                                        {parseInsidePages(sub.insidepage).map(
                                          (action, actionIndex) => (
                                            <div
                                              key={actionIndex}
                                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                            >
                                              <Checkbox
                                                id={`mobile-app-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                checked={isMobileAppPermissionEnabled(
                                                  sub.page_url_id,
                                                  action.id,
                                                )}
                                                onCheckedChange={() =>
                                                  handleMobileAppPermissionToggle(
                                                    sub.page_url_id,
                                                    action.id,
                                                  )
                                                }
                                              />
                                              <Label
                                                htmlFor={`mobile-app-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                className="text-sm cursor-pointer"
                                              >
                                                {action.label}
                                              </Label>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                          {parent.parent_id === 0 &&
                            !parent.dropdown &&
                            parent.insidepage && (
                              <div className="p-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-4">
                                  {parseInsidePages(parent.insidepage).map(
                                    (action, actionIndex) => (
                                      <div
                                        key={actionIndex}
                                        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                      >
                                        <Checkbox
                                          id={`mobile-app-parent-action-${parent.id}-${actionIndex}`}
                                          checked={isMobileAppPermissionEnabled(
                                            parent.page_url_id,
                                            action.id,
                                          )}
                                          onCheckedChange={() =>
                                            handleMobileAppPermissionToggle(
                                              parent.page_url_id,
                                              action.id,
                                            )
                                          }
                                        />
                                        <Label
                                          htmlFor={`mobile-app-parent-action-${parent.id}-${actionIndex}`}
                                          className="text-sm cursor-pointer"
                                        >
                                          {action.label}
                                        </Label>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Mobile App Landing URL */}
                  {Object.keys(mobileAppPermissions).length > 0 && (
                    <div className="pt-4 border-t space-y-2">
                      <Label>
                        Mobile App Landing URL{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="/dashboard or /analytics"
                          value={mobileAppLandingUrl}
                          onChange={(e) => {
                            handleURLChange_Modify(e);
                            setMobileAppLandingUrl(e.target.value);
                            setErrors((prev) => {
                              const { mobileAppLandingUrl, ...rest } = prev;
                              return rest;
                            });
                          }}
                          className={cn(
                            "pl-9",
                            errors.mobileAppLandingUrl && "border-destructive",
                          )}
                        />
                      </div>
                      {errors.mobileAppLandingUrl && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.mobileAppLandingUrl}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        The page where users will land when accessing Mobile App
                      </p>
                    </div>
                  )}
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
          {/* Mobile Pos Permissions Accordion - Close */}

          {/* Manager App Permissions Accordion - Start */}
          <AccordionItem value="manager_app" className="border rounded-lg">
            <Card className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-success" />
                    <div className="text-left">
                      <p className="font-semibold">Manager App Permissions</p>
                      <p className="text-sm text-muted-foreground font-normal">
                        Choose which sections and actions this role can access
                        in Manager App
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {Object.keys(managerAppPermissions).length} permission(s)
                      selected
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent className="pt-0 space-y-6">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllManagerAppPermissions}
                      className="gap-2"
                    >
                      {Object.keys(managerAppPermissions).length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {managerAppParentData.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No Manager App permissions available
                      </p>
                    ) : (
                      managerAppParentData.map((parent) => (
                        <div
                          key={parent.id}
                          className="border rounded-lg overflow-hidden"
                        >
                          <div
                            className={cn(
                              "p-4 bg-muted/30 border-b transition-colors",
                              isManagerAppPermissionEnabled(
                                parent.page_url_id,
                              ) && "bg-success/5",
                            )}
                          >
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`dash-parent-${parent.id}`}
                                checked={isManagerAppPermissionEnabled(
                                  parent.page_url_id,
                                )}
                                onCheckedChange={() =>
                                  handleManagerAppPermissionToggle(
                                    parent.page_url_id,
                                  )
                                }
                              />
                              <Label
                                htmlFor={`dash-parent-${parent.id}`}
                                className="text-base font-semibold cursor-pointer flex-1"
                              >
                                {parent.page_name}
                              </Label>
                              {isDashboardPermissionEnabled(
                                parent.page_url_id,
                              ) && <Shield className="w-4 h-4 text-success" />}
                            </div>
                          </div>

                          {managerAppSubData[parent.id] &&
                            managerAppSubData[parent.id].length > 0 && (
                              <div className="p-4 space-y-4">
                                {managerAppSubData[parent.id].map((sub) => (
                                  <div key={sub.id} className="space-y-3">
                                    <div className="flex items-center space-x-2 pl-4">
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                      <Checkbox
                                        id={`manager-sub-${parent.id}-${sub.id}`}
                                        checked={isManagerAppPermissionEnabled(
                                          sub.page_url_id,
                                        )}
                                        onCheckedChange={() =>
                                          handleManagerAppPermissionToggle(
                                            sub.page_url_id,
                                          )
                                        }
                                      />
                                      <Label
                                        htmlFor={`dash-sub-${parent.id}-${sub.id}`}
                                        className="font-medium cursor-pointer flex-1"
                                      >
                                        {sub.page_name}
                                      </Label>
                                    </div>

                                    {sub.insidepage && (
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-12">
                                        {parseInsidePages(sub.insidepage).map(
                                          (action, actionIndex) => (
                                            <div
                                              key={actionIndex}
                                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                            >
                                              <Checkbox
                                                id={`manager-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                checked={isManagerAppPermissionEnabled(
                                                  sub.page_url_id,
                                                  action.id,
                                                )}
                                                onCheckedChange={() =>
                                                  handleManagerAppPermissionToggle(
                                                    sub.page_url_id,
                                                    action.id,
                                                  )
                                                }
                                              />
                                              <Label
                                                htmlFor={`dash-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                className="text-sm cursor-pointer"
                                              >
                                                {action.label}
                                              </Label>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                          {parent.parent_id === 0 &&
                            !parent.dropdown &&
                            parent.insidepage && (
                              <div className="p-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-4">
                                  {parseInsidePages(parent.insidepage).map(
                                    (action, actionIndex) => (
                                      <div
                                        key={actionIndex}
                                        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                      >
                                        <Checkbox
                                          id={`manager-parent-action-${parent.id}-${actionIndex}`}
                                          checked={isManagerAppPermissionEnabled(
                                            parent.page_url_id,
                                            action.id,
                                          )}
                                          onCheckedChange={() =>
                                            handleManagerAppPermissionToggle(
                                              parent.page_url_id,
                                              action.id,
                                            )
                                          }
                                        />
                                        <Label
                                          htmlFor={`manager-parent-action-${parent.id}-${actionIndex}`}
                                          className="text-sm cursor-pointer"
                                        >
                                          {action.label}
                                        </Label>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Dashboard Landing URL */}
                  {Object.keys(managerAppPermissions).length > 0 && (
                    <div className="pt-4 border-t space-y-2">
                      <Label>
                        Manager App Landing URL{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="/dashboard or /analytics"
                          value={managerAppLandingUrl}
                          onChange={(e) => {
                            handleURLChange_Modify(e);
                            setManagerAppLandingUrl(e.target.value);
                            setErrors((prev) => {
                              const { managerAppLandingUrl, ...rest } = prev;
                              return rest;
                            });
                          }}
                          className={cn(
                            "pl-9",
                            errors.managerAppLandingUrl && "border-destructive",
                          )}
                        />
                      </div>
                      {errors.managerAppLandingUrl && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.managerAppLandingUrl}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        The page where users will land when accessing Manager App
                      </p>
                    </div>
                  )}
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
          {/* Manager App Permissions Accordion - Close */}

          {/* Captain App Accordion - Start */}
          <AccordionItem value="captain_app" className="border rounded-lg">
            <Card className="border-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-success" />
                    <div className="text-left">
                      <p className="font-semibold">Captain App Permissions</p>
                      <p className="text-sm text-muted-foreground font-normal">
                        Choose which sections and actions this role can access
                        in Captain App
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {Object.keys(captainAppPermissions).length} permission(s)
                      selected
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent className="pt-0 space-y-6">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllCaptainAppPermissions}
                      className="gap-2"
                    >
                      {Object.keys(captainAppPermissions).length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {captainAppParentData.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No Captain App permissions available
                      </p>
                    ) : (
                      captainAppParentData.map((parent) => (
                        <div
                          key={parent.id}
                          className="border rounded-lg overflow-hidden"
                        >
                          <div
                            className={cn(
                              "p-4 bg-muted/30 border-b transition-colors",
                              isCaptainAppPermissionEnabled(
                                parent.page_url_id,
                              ) && "bg-success/5",
                            )}
                          >
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`captain-app-parent-${parent.id}`}
                                checked={isCaptainAppPermissionEnabled(
                                  parent.page_url_id,
                                )}
                                onCheckedChange={() =>
                                  handleCaptainAppPermissionToggle(
                                    parent.page_url_id,
                                  )
                                }
                              />
                              <Label
                                htmlFor={`captain-app-parent-${parent.id}`}
                                className="text-base font-semibold cursor-pointer flex-1"
                              >
                                {parent.page_name}
                              </Label>
                              {isCaptainAppPermissionEnabled(
                                parent.page_url_id,
                              ) && <Shield className="w-4 h-4 text-success" />}
                            </div>
                          </div>

                          {captainAppSubData[parent.id] &&
                            captainAppSubData[parent.id].length > 0 && (
                              <div className="p-4 space-y-4">
                                {captainAppSubData[parent.id].map((sub) => (
                                  <div key={sub.id} className="space-y-3">
                                    <div className="flex items-center space-x-2 pl-4">
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                      <Checkbox
                                        id={`captain-app-sub-${parent.id}-${sub.id}`}
                                        checked={isCaptainAppPermissionEnabled(
                                          sub.page_url_id,
                                        )}
                                        onCheckedChange={() =>
                                          handleCaptainAppPermissionToggle(
                                            sub.page_url_id,
                                          )
                                        }
                                      />
                                      <Label
                                        htmlFor={`captain-app-sub-${parent.id}-${sub.id}`}
                                        className="font-medium cursor-pointer flex-1"
                                      >
                                        {sub.page_name}
                                      </Label>
                                    </div>

                                    {sub.insidepage && (
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-12">
                                        {parseInsidePages(sub.insidepage).map(
                                          (action, actionIndex) => (
                                            <div
                                              key={actionIndex}
                                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                            >
                                              <Checkbox
                                                id={`captain-app-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                checked={isCaptainAppPermissionEnabled(
                                                  sub.page_url_id,
                                                  action.id,
                                                )}
                                                onCheckedChange={() =>
                                                  handleCaptainAppPermissionToggle(
                                                    sub.page_url_id,
                                                    action.id,
                                                  )
                                                }
                                              />
                                              <Label
                                                htmlFor={`captain-app-action-${parent.id}-${sub.id}-${actionIndex}`}
                                                className="text-sm cursor-pointer"
                                              >
                                                {action.label}
                                              </Label>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                          {parent.parent_id === 0 &&
                            !parent.dropdown &&
                            parent.insidepage && (
                              <div className="p-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-4">
                                  {parseInsidePages(parent.insidepage).map(
                                    (action, actionIndex) => (
                                      <div
                                        key={actionIndex}
                                        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                      >
                                        <Checkbox
                                          id={`captain-app-parent-action-${parent.id}-${actionIndex}`}
                                          checked={isCaptainAppPermissionEnabled(
                                            parent.page_url_id,
                                            action.id,
                                          )}
                                          onCheckedChange={() =>
                                            handleCaptainAppPermissionToggle(
                                              parent.page_url_id,
                                              action.id,
                                            )
                                          }
                                        />
                                        <Label
                                          htmlFor={`captain-app-parent-action-${parent.id}-${actionIndex}`}
                                          className="text-sm cursor-pointer"
                                        >
                                          {action.label}
                                        </Label>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Captain App Landing URL */}
                  {Object.keys(captainAppPermissions).length > 0 && (
                    <div className="pt-4 border-t space-y-2">
                      <Label>
                        Captain App Landing URL{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="/dashboard or /analytics"
                          value={captainAppLandingUrl}
                          onChange={(e) => {
                            handleURLChange_Modify(e);
                            setCaptainAppLandingUrl(e.target.value);
                            setErrors((prev) => {
                              const { captainAppLandingUrl, ...rest } = prev;
                              return rest;
                            });
                          }}
                          className={cn(
                            "pl-9",
                            errors.captainAppLandingUrl && "border-destructive",
                          )}
                        />

                        
                      </div>
                      {errors.captainAppLandingUrl && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.captainAppLandingUrl}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        The page where users will land when accessing Captain App
                      </p>
                    </div>
                  )}
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
          {/* Captain App Accordion - Close */}

        </Accordion>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/roles-permissions")}
            disabled={showLoaderAdmin}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={showLoaderAdmin}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            {showLoaderAdmin ? (
              "Saving..."
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? "Update Role" : "Create Role"}
              </>
            )}
          </Button>
        </div>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
